import {isCustomizableProductSet} from '../../../utils/product-utils'
import {useHistory, useLocation} from 'react-router-dom'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX} from '../../../constants'

/**
 * Hook to get stored configuration for customizable product set
 *
 * **Warning:** This hook is tightly coupled with useSelectableSetProduct hook,
 * so any changes should be tested in conjunction with useSelectableSetProduct
 *
 * @param {Object} product
 * @return {{
 *   isAvailable: boolean,
 *   apply: function(): void,
 *   clear: function(): void,
 * }}
 */
const useStoredConfiguration = (product) => {
    const history = useHistory()
    const {pathname, search} = useLocation()
    // flag if configuration storage is enabled
    const isEnabled = useMemo(() => isCustomizableProductSet(product), [product])
    // local storage key for configuration
    const storageKey = useMemo(() => {
        if (!isEnabled) return null

        // remove the suffix from pathname
        const idx = pathname.lastIndexOf('.')
        let key = idx > 0 ? pathname.slice(0, idx) : pathname

        // remove the starting slash
        if (key.startsWith('/')) {
            key = key.slice(1)
        }

        return `${CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX}${key}`
    }, [isEnabled, pathname])
    // prepare configuration keys
    const configurationKeys = useMemo(
        () =>
            new Set(
                [...(product?.setAccessories || []), ...(product?.setExtensions || [])]
                    .map((p) => p?.setProductId)
                    .filter(Boolean)
            ),
        [product]
    )
    // current configuration from search params
    const configuration = useMemo(() => {
        if (!isEnabled) return null
        // parse search params
        const params = new URLSearchParams(search)
        // filter configuration params
        const configurationParams = new URLSearchParams(
            [...params.entries()].filter(([key]) => configurationKeys.has(key))
        )
        return configurationParams.toString()
    }, [isEnabled, search, configurationKeys])

    // save configuration to local storage when it changes
    useEffect(() => {
        if (configuration) {
            localStorage.setItem(storageKey, configuration)
        }
    }, [configuration, storageKey])

    // Note 1: use state with effect instead of memo to prevent mismatch between ssr and client
    // Note 2: keep the value in an object with pathname as key to not reuse it when pathname changes
    // - this happens when navigating from one product to another but the effect triggers after the render
    // which causes isAvailable to be true for the new product url, but using the old configuration
    const [storedConfigurationForPathname, setStoredConfigurationForPathname] = useState(null)
    useEffect(() => {
        setStoredConfigurationForPathname(
            storageKey ? {[pathname]: localStorage.getItem(storageKey)} : null
        )
    }, [pathname, storageKey])

    // flag if stored configuration is available
    const isAvailable = useMemo(
        () => Boolean(isEnabled && storedConfigurationForPathname?.[pathname] && !configuration),
        [configuration, isEnabled, pathname, storedConfigurationForPathname]
    )

    // apply stored configuration
    const apply = useCallback(() => {
        if (isAvailable && storedConfigurationForPathname?.[pathname]) {
            history.replace(`${pathname}?${storedConfigurationForPathname[pathname]}`)
        }
    }, [isAvailable, history, pathname, storedConfigurationForPathname])

    // clear stored configuration
    const clear = useCallback(() => {
        if (storageKey) {
            localStorage.removeItem(storageKey)
            setStoredConfigurationForPathname(null)
        }
    }, [storageKey])

    return {
        isAvailable,
        apply,
        clear
    }
}

export default useStoredConfiguration
